import { Card, CardBody, Col, Container, Row, Table, Button } from "reactstrap";
import React, { useEffect, useState } from "react";

import API from "../../utils/API";
import AdminSelect from "../../components/formfields/AdminSelect";
import DateRange from "../../components/formfields/DateRange";
import LenderSelect from "../../components/formfields/LenderSelect";
import LenderTitle from "../../components/fields/LenderTitle";
import Loader from "../../components/Loader";
import MoneyTotal from "../../components/fields/MoneyTotal";
import ProductMulti from "../../components/formfields/ProductMulti";
import SummaryChart from "../lenders/SummaryChart";
import dateFormat from "dateformat";
import { parseFilterValues } from "../../utils/helpers/filterHelper";
import { useHistory } from "react-router-dom";
import SortableTable from "../../components/SortableTable";
import CSVDownload from "../../components/CsvDownload";

const LenderSummary = () => {
  const history = useHistory();
  const defaultDate =
    dateFormat(new Date().setDate(new Date().getDate() - 6), "mmm d, yyyy") +
    " to " +
    dateFormat(new Date(), "mmm d, yyyy");
  const defaultFiltrationState = {
    date: defaultDate,
  };

  const getParsedFilters = (searchString) => {
    const search = searchString !== undefined ? searchString : history.location.search;
    const urlParams = new URLSearchParams(search);
    const parsedParams = Object.fromEntries(urlParams.entries());
    const filtersParsed = parseFilterValues(parsedParams);
    return { ...defaultFiltrationState, ...filtersParsed };
  };

  const [filtration, setFiltration] = useState(() => getParsedFilters());
  const [appliedFiltration, setAppliedFiltration] = useState(null);
  const [lenders, setLenders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [dataDate, setDataDate] = useState(null);

  const loadResults = (filters) => {
    if (!filters?.date) return;
    setLoading(true);
    API.post("/admin/lender/summarylender", filters)
      .then((response) => {
        const payload = response.data?.data;
        setData(payload?.report ?? []);
        setDataDate(payload?.reportDate ?? null);
        setAppliedFiltration(filters);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const filtersChanged = appliedFiltration !== null &&
    JSON.stringify(filtration) !== JSON.stringify(appliedFiltration);

  useEffect(() => {
    const unlisten = history.listen((location) => {
      setFiltration(getParsedFilters(location.search));
    });
    return () => unlisten();
  }, [history]);

  const buildSearch = (nextFiltration) => {
    const searchParams = new URLSearchParams();
    Object.entries(nextFiltration).forEach(([key, val]) => {
      if (val === undefined || val === null) return;
      if (Array.isArray(val)) {
        if (val.length) searchParams.set(key, val.map(String).join(","));
      } else {
        searchParams.set(key, String(val));
      }
    });
    return searchParams.toString();
  };

  let totalLeads = 0,
    totalMatched = 0,
    totalUnmatched = 0,
    totalError = 0,
    totalReturned = 0,
    totalTimeout = 0,
    totalTotal = 0;

  const changeData = (name, value) => {
    const nextFiltration = { ...filtration, [name]: value };
    setFiltration(nextFiltration);
    history.push({
      pathname: history.location.pathname,
      search: buildSearch(nextFiltration),
    });
  };

  const changeNativeData = (e) => {
    const nextFiltration = { ...filtration, [e.target.name]: e.target.value };
    setFiltration(nextFiltration);
    history.push({
      pathname: history.location.pathname,
      search: buildSearch(nextFiltration),
    });
  };

  const columns = [
    {
      name: "Lender",
      selector: (row) => row.lender.title.toLowerCase(),
      cell: (row) => (
        <LenderTitle title={row.lender.title} id={row.lender.id} />
      ),
      sortable: true,
    },
    {
      name: "Sent",
      selector: (row) => row.leads,
      cell: (row) => <span className="font-weight-bold">{row.leads}</span>,
      sortable: true,
    },
    {
      name: "Matched",
      selector: (row) => row.matched,
      cell: (row) => (
        <span
          className={`font-weight-bold ${row.matched ? "text-success" : ""}`}
        >
          {row.matched}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Unmatched",
      selector: (row) => row.unmatched,
      sortable: true,
    },
    {
      name: "Returned",
      selector: (row) => row.returned,
      cell: (row) => (
        <span className={`${row.returned ? "text-primary" : ""}`}>
          {row.returned}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Error",
      selector: (row) => row.error,
      cell: (row) => (
        <span className={`${row.error ? "text-warning" : ""}`}>
          {row.error}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Timeout",
      selector: (row) => row.timeout,
      cell: (row) => (
        <span className={`${row.timeout ? "text-danger" : ""}`}>
          {row.timeout}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Total",
      selector: (row) => row.total,
      cell: (row) => (row.total ? <MoneyTotal money={row.total} /> : "$0"),
      sortable: true,
    },
  ];

  return (
    <Container fluid className="p-0">
      {filtersChanged && (
        <style>{`
          @keyframes show-results-pulse {
            0%, 100% { box-shadow: 0 0 12px 2px rgba(0, 123, 255, 0.5); }
            50% { box-shadow: 0 0 20px 5px rgba(0, 123, 255, 0.85); }
          }
        `}</style>
      )}
      <div className="row justify-content-between">
        <div className="col-auto">
          <h3 className="h3 mb-3 float-left">Lenders summary</h3>
        </div>
        {!loading && (
          <div className="col-auto">
            <CSVDownload
                url="/admin/lender/summarylenderexport"
                filename="LendersSummary"
                filtration={appliedFiltration ?? filtration}
            />
          </div>
        )}
      </div>
      {loading ? (
        <Loader />
      ) : (
        <>
          <Row form className={"mb-2"}>
            <Col md={3}>
              <DateRange
                onChange={changeData}
                name={"date"}
                value={filtration?.date}
              />
            </Col>
            <Col md={3}>
              <ProductMulti
                name={"product"}
                onChange={changeData}
                value={filtration?.product}
              />
            </Col>
            <Col md={3}>
              <LenderSelect
                name={"lender"}
                onChange={changeData}
                lenders={lenders}
                value={filtration?.lender}
              />
            </Col>
            <Col md={3}>
              <AdminSelect
                name={"representative"}
                onChange={changeNativeData}
                required={false}
                value={filtration?.representative}
                placeholder={"Account representative..."}
              />
            </Col>
          </Row>
          <div className="mb-3 text-center mt-4">
            <Button
              color="primary"
              onClick={() => loadResults(filtration)}
              style={filtersChanged ? {
                boxShadow: "0 0 16px 3px rgba(0, 123, 255, 0.7)",
                animation: "show-results-pulse 1.5s ease-in-out infinite",
              } : undefined}
            >
              Show results
            </Button>
          </div>
        </>
      )}
      {!loading && data && data.length > 0 ? (
        <>
          {dataDate.length > 2 && (
            <div>
              <SummaryChart inputData={dataDate} />
            </div>
          )}
          <SortableTable columns={columns} data={data} pagination={false} />
          <Card className="flex-fill w-100 p-3">
            {data.map((dataItem, dataIndex) => {
              totalLeads += dataItem.leads;
              totalMatched += dataItem.matched;
              totalUnmatched += dataItem.unmatched;
              totalError += dataItem.error;
              totalReturned += dataItem.returned;
              totalTimeout += dataItem.timeout;
              totalTotal += dataItem.total;
            })}
            <h4>Summary:</h4>
            <ul className="bull list-unstyled mb-0">
              <li>
                Sent: <span className="font-weight-bold">{totalLeads}</span>
              </li>
              <li>
                Matched:{" "}
                <span
                  className={`font-weight-bold ${
                    totalMatched ? "text-success" : ""
                  }`}
                >
                  {totalMatched}
                </span>
              </li>
              <li>
                Unmatched:{" "}
                <span className="font-weight-bold">{totalUnmatched}</span>
              </li>
              <li>
                Returned:{" "}
                <span
                  className={`font-weight-bold ${
                    totalReturned ? "text-primary" : ""
                  }`}
                >
                  {totalReturned}
                </span>
              </li>
              <li>
                Error:{" "}
                <span
                  className={`font-weight-bold ${
                    totalError ? "text-warning" : ""
                  }`}
                >
                  {totalError}
                </span>
              </li>
              <li>
                Timeout:{" "}
                <span
                  className={`font-weight-bold ${
                    totalTimeout ? "text-danger" : ""
                  }`}
                >
                  {totalTimeout}
                </span>
              </li>
              <li>
                Total:{" "}
                <span className="font-weight-bold">
                  {totalTotal ? <MoneyTotal money={totalTotal} /> : "$0"}
                </span>
              </li>
            </ul>
          </Card>
        </>
      ) : (
        !loading && (
          <CardBody className="text-center">
            {appliedFiltration === null ? (
              <h5>Click &quot;Show results&quot; to load data</h5>
            ) : (
              <h5>Data not found</h5>
            )}
          </CardBody>
        )
      )}
    </Container>
  );
};

export default LenderSummary;
